/**
 * UserProfilePage - страница профиля текущего пользователя, общая для /admin и /dashboard.
 *
 * Данные - только из /api/v2/profile: пользователя определяет сервер по сессии (PROJECT_NOTES §35).
 * Роль (название, цвет бейджа, интерфейс, стартовая страница) и заполненность профиля приходят с сервера,
 * различия /admin и /dashboard - только по role.targetInterface, без проверок slug роли.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { WarningAmber } from '@mui/icons-material';
import { PROFILE_WIDE, ZERDE_COLORS } from './constants';
import { applyProfileStatus, isCurrentTimeWithinWorkingHours, isSyncBlocking } from './utils';
import { deleteAvatar, fetchProfileMe, fetchSyncStatus, ProfileMe, ProfileStartPage, SyncStatus, uploadAvatar } from './profileApi';
import { getDataUrlSizeKB } from './avatarCompressor';
import { apiErrorMessage } from '../admin/reports/edit/editApi';
import { notify } from '../notifications';
import { findStartPage } from '../../config/startPages';

import ProfileSidebar, { ProfileTab, SidebarTab } from './sections/ProfileSidebar';
import ProfileInfoSection from './sections/ProfileInfoSection';
import PasswordChangeSection from './sections/PasswordChangeSection';
import PersonalDataSection from './sections/PersonalDataSection';
import NotificationSettingsSection from './sections/NotificationSettingsSection';
import MailIntegrationSection from './sections/MailIntegrationSection';
import BiometricsSection from './sections/BiometricsSection';
import SessionsDevicesSection from './sections/SessionsDevicesSection';
import ActivitySection from './sections/ActivitySection';
import MyPermissionsSection from './sections/MyPermissionsSection';
import ServicesCacheSection from './sections/ServicesCacheSection';

interface UserProfilePageProps {
  username: string;
  /** Не используется: номер аптеки и роль берутся с сервера. Оставлены для совместимости вызовов */
  pharmacyNum?: string | null;
  userRole?: string | null;
  forceCheck?: boolean;
  onConfirm?: () => void;
}

const SECTION_IDS: { id: string; key: ProfileTab }[] = [
  { id: 'section-profile', key: 'profile' },
  { id: 'section-password', key: 'password' },
  { id: 'section-personal', key: 'personal' },
  { id: 'section-notifications', key: 'notifications' },
  { id: 'section-mail', key: 'mail' },
  { id: 'section-biometrics', key: 'biometrics' },
  { id: 'section-sessions', key: 'sessions' },
  { id: 'section-activity', key: 'activity' },
  { id: 'section-permissions', key: 'permissions' },
  { id: 'section-services', key: 'services' }
];

/** Аватар - data URL до 300 КБ: если localStorage его не вместит, шапка возьмет аватар из /summary */
function storeAvatar(avatarUrl: string | null) {
  try {
    if (avatarUrl) localStorage.setItem('userAvatar', avatarUrl);
    else localStorage.removeItem('userAvatar');
  } catch {
    try { localStorage.removeItem('userAvatar'); } catch { /* кэш шапки необязателен */ }
  }
  window.dispatchEvent(new CustomEvent('avatarUpdated', { detail: { avatarUrl } }));
}

/** Имя и аватар для шапки панели, guid геолокации - если его еще нет на устройстве */
function publishIdentity(me: ProfileMe) {
  const fullName = me.profile.fullName.trim();
  if (fullName) localStorage.setItem('fullName', fullName);
  else localStorage.removeItem('fullName');
  window.dispatchEvent(new CustomEvent('profileUpdated', { detail: { fullName } }));
  storeAvatar(me.profile.avatarUrl);
  if (me.profile.geoGuid && !localStorage.getItem('guid')) localStorage.setItem('guid', me.profile.geoGuid);
}

/** Точка продаж из профиля; без точки - по интерфейсу роли */
function locationOf(me: ProfileMe): string {
  const num = me.profile.pharmacyNum.trim();
  if (num && num !== '-' && num !== 'NaN' && num !== '0') {
    return me.profile.regionName ? `Точка №${num} · ${me.profile.regionName}` : `Точка №${num}`;
  }
  return me.role.targetInterface === 'admin' ? 'Офис' : 'Аптека не привязана';
}

/** Синхронизатор касс проверяется только в кабинете аптеки; ошибка проверки не блокирует работу */
async function loadSync(me: ProfileMe): Promise<SyncStatus | null> {
  if (me.role.targetInterface !== 'dashboard') return null;
  try {
    return await fetchSyncStatus(me.profile.kkm, me.profile.username);
  } catch (err) {
    console.warn('Проверка статуса синхронизатора:', err);
    return null;
  }
}

export const UserProfilePage: React.FC<UserProfilePageProps> = ({ username: propsUsername }) => {
  const username = propsUsername || localStorage.getItem('username') || '';

  const [me, setMe] = useState<ProfileMe | null>(null);
  const [sync, setSync] = useState<SyncStatus | null>(null);
  const syncRef = useRef<SyncStatus | null>(null);
  const [syncChecking, setSyncChecking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ProfileTab>('profile');

  const applyMe = useCallback((next: ProfileMe, nextSync: SyncStatus | null) => {
    setMe(next);
    publishIdentity(next);
    applyProfileStatus(next, isSyncBlocking(nextSync, next.profile.workingHours));
  }, []);

  /** Профиль и статус синхронизатора; ошибку обрабатывает вызывающий */
  const loadAll = useCallback(async () => {
    const next = await fetchProfileMe();
    const nextSync = await loadSync(next);
    syncRef.current = nextSync;
    setSync(nextSync);
    applyMe(next, nextSync);
  }, [applyMe]);

  /** Повторное чтение профиля после изменений в разделе; статус синхронизатора не перепроверяется */
  const reloadMe = useCallback(async () => {
    try {
      applyMe(await fetchProfileMe(), syncRef.current);
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось обновить данные профиля'));
    }
  }, [applyMe]);

  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    loadAll()
      .catch(err => setLoadError(apiErrorMessage(err, 'Не удалось загрузить профиль')))
      .finally(() => setLoading(false));
  }, [loadAll, username]);

  const hasMail = Boolean(me && me.mail);

  // Подсветка пункта бокового меню при прокрутке
  useEffect(() => {
    if (loading || !me) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const found = SECTION_IDS.find(s => s.id === entry.target.id);
          if (found) setActiveTab(found.key);
        });
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );
    SECTION_IDS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [loading, me, hasMail]);

  const handleScrollToSection = (tabKey: ProfileTab) => {
    const section = SECTION_IDS.find(s => s.key === tabKey);
    setActiveTab(tabKey);
    if (section) document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleAvatarSave = async (dataUrl: string) => {
    const avatarUrl = await uploadAvatar(dataUrl);
    setMe(prev => (prev ? { ...prev, profile: { ...prev.profile, avatarUrl } } : prev));
    storeAvatar(avatarUrl);
    notify.success(`Аватар сохранен (${getDataUrlSizeKB(avatarUrl)} КБ)`);
  };

  const handleAvatarDelete = async () => {
    const message = await deleteAvatar();
    setMe(prev => (prev ? { ...prev, profile: { ...prev.profile, avatarUrl: null } } : prev));
    storeAvatar(null);
    notify.info(message);
  };

  /** Панель берет стартовую страницу из localStorage при следующем входе и перезагрузке */
  const handleStartPageSaved = (next: ProfileStartPage) => {
    setMe(prev => (prev ? { ...prev, startPage: next } : prev));
    localStorage.setItem('startPage', next.current);
  };

  const handleRefreshSync = async () => {
    if (!me) return;
    setSyncChecking(true);
    try {
      const status = await fetchSyncStatus(me.profile.kkm, me.profile.username);
      syncRef.current = status;
      setSync(status);
      applyProfileStatus(me, isSyncBlocking(status, me.profile.workingHours));
      if (status.isRunning) {
        notify.success('Синхронизатор запущен, служба обмена активна');
      } else if (isCurrentTimeWithinWorkingHours(me.profile.workingHours)) {
        notify.error('Служба «Синхронизатор» не запущена. Запустите службу на кассе для перехода в другие разделы');
      } else {
        notify.warning('Служба «Синхронизатор» не запущена. Сейчас нерабочее время - навигация не блокируется');
      }
    } catch (err) {
      notify.warning(apiErrorMessage(err, 'Не удалось получить статус синхронизатора'));
    } finally {
      setSyncChecking(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, gap: 2 }}>
        <CircularProgress sx={{ color: ZERDE_COLORS.primary }} />
        <Typography variant="body2" color="text.secondary">Загрузка профиля...</Typography>
      </Box>
    );
  }

  if (!me) {
    return (
      <Alert severity="error" sx={{ borderRadius: '12px' }}>
        {loadError || 'Не удалось загрузить профиль'}
      </Alert>
    );
  }

  const { profile, role, completeness } = me;
  const isDashboard = role.targetInterface === 'dashboard';
  const syncBlocked = isSyncBlocking(sync, profile.workingHours);
  const missing = completeness.missing;
  const onlySync = syncBlocked && missing.length === 0;
  const mailPending = completeness.items.some(item => item.key === 'mail' && !item.done);

  // Текущая страница - личный выбор или страница роли (решает сервер); available пуст у не перезапущенного сервера
  const startPage = findStartPage(role.targetInterface, me.startPage.current) || null;
  const startPageBlocked = Boolean(startPage
    && me.startPage.available.length > 0 && me.startPage.available.indexOf(startPage.key) < 0);

  const tabs: SidebarTab[] = [
    { key: 'profile', alert: syncBlocked ? 'Служба' : undefined, alertTone: 'error' },
    { key: 'password' },
    { key: 'personal', alert: completeness.personalComplete ? undefined : 'Заполнить' },
    { key: 'notifications' },
    ...(hasMail ? [{ key: 'mail' as ProfileTab, alert: mailPending ? 'Подключить' : undefined }] : []),
    { key: 'biometrics' },
    { key: 'sessions' },
    { key: 'activity' },
    { key: 'permissions' },
    { key: 'services' }
  ];

  return (
    // Внешний блок - контейнер `profile`: две колонки включаются по его ширине (PROFILE_WIDE), а не по ширине окна
    <Box sx={{ width: '100%', containerType: 'inline-size', containerName: 'profile' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, [PROFILE_WIDE]: { flexDirection: 'row', alignItems: 'flex-start' } }}>
        <ProfileSidebar
          displayName={profile.fullName || profile.username}
          role={role}
          locationText={locationOf(me)}
          avatarUrl={profile.avatarUrl}
          completeness={completeness}
          tabs={tabs}
          activeTab={activeTab}
          onTabClick={handleScrollToSection}
          onAvatarSave={handleAvatarSave}
          onAvatarDelete={handleAvatarDelete}
        />

        {/* Колонка разделов - контейнер: секции раскладываются по ее ширине, а не по ширине окна */}
        <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5, pb: 10, containerType: 'inline-size' }}>
          {(missing.length > 0 || syncBlocked) && (
            <Alert
              severity={onlySync ? 'error' : 'warning'}
              icon={<WarningAmber fontSize="inherit" />}
              sx={{
                borderRadius: '14px',
                border: onlySync ? '1.5px solid #fecaca' : '1px solid #fed7aa',
                bgcolor: onlySync ? '#fef2f2' : '#fff7ed',
                color: onlySync ? '#991b1b' : '#9a3412',
                '& .MuiAlert-icon': { color: onlySync ? '#dc2626' : '#ea580c' }
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {onlySync
                  ? 'Служба «Синхронизатор» не запущена (Выгрузка прекращена)'
                  : 'Требуется заполнение обязательных разделов профиля'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {onlySync
                  ? 'На кассовом компьютере остановлена служба синхронизатора. Для перехода в другие разделы портала запустите службу на кассе.'
                  : `Не выполнено: ${missing.concat(syncBlocked ? ['запуск службы «Синхронизатор»'] : []).join(', ')}. `
                    + 'Переход в другие разделы откроется после выполнения всех условий.'}
              </Typography>
            </Alert>
          )}

          {loadError && (
            <Alert severity="error" sx={{ borderRadius: '12px' }}>{loadError}</Alert>
          )}

          <ProfileInfoSection
            me={me}
            locationText={locationOf(me)}
            sync={sync}
            syncChecking={syncChecking}
            onRefreshSync={handleRefreshSync}
            startPage={startPage}
            startPageBlocked={startPageBlocked}
            onStartPageSaved={handleStartPageSaved}
          />

          <PasswordChangeSection
            username={profile.username}
            passwordUpdatedAt={profile.passwordUpdatedAt}
            onChanged={reloadMe}
          />

          <PersonalDataSection
            profile={profile}
            requireWorkingHours={isDashboard}
            completeness={completeness}
            onSaved={reloadMe}
          />

          <NotificationSettingsSection />

          {me.mail && (
            <MailIntegrationSection
              username={profile.username}
              mail={me.mail}
              required={completeness.items.some(item => item.key === 'mail')}
              onChanged={reloadMe}
            />
          )}

          <div id="section-biometrics">
            <BiometricsSection onStatusChange={() => { reloadMe(); }} />
          </div>

          <SessionsDevicesSection />

          <ActivitySection />

          <MyPermissionsSection />

          <ServicesCacheSection
            username={profile.username}
            pharmacyNum={profile.pharmacyNum || null}
            pharmacyAddress={profile.pharmacyAddress}
            latitude={profile.latitude}
            longitude={profile.longitude}
            accuracy={profile.accuracy}
            geoUnavailable={Boolean(profile.geoUnavailable)}
            onGeoUpdated={(geo) => {
              setMe(prev => (prev ? {
                ...prev,
                profile: {
                  ...prev.profile,
                  latitude: Number(geo.latitude).toFixed(5),
                  longitude: Number(geo.longitude).toFixed(5),
                  accuracy: geo.accuracy != null ? geo.accuracy : prev.profile.accuracy,
                  // пустой адрес - сервер не определил его по новым координатам, старый к ним не относится
                  address: geo.address != null ? geo.address : prev.profile.address,
                  pharmacyAddress: geo.address != null ? geo.address : prev.profile.pharmacyAddress
                }
              } : prev));
            }}
            onSyncData={loadAll}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default UserProfilePage;
